<script setup>
import { ref, computed } from "vue";

import { useTriviaStore } from "@stores/trivia";
import IconTrophy from "@components/icons/Trophy.vue";
import IconCheckmark from "@components/icons/Checkmark.vue";
import IconClock from "@components/icons/Clock.vue";
import IconInfoCircle from "@components/icons/InfoCircle.vue";
import Button from "@components/Button.vue";
import TextInput from "@components/TextInput.vue";

const triviaStore = useTriviaStore();
const email = ref("");
const username = ref("");
const marketing = ref(false);
const submitting = ref(false);
const submitted = ref(false);
const submitErrors = ref({});

const canSubmit = computed(
    () => username.value.trim() !== "" && email.value.trim() !== "",
);

async function handleSubmit() {
    if (!canSubmit.value || submitting.value || submitted.value) return;
    submitting.value = true;
    submitErrors.value = {};
    try {
        await triviaStore.submitScore({
            username: username.value.trim(),
            email: email.value.trim(),
            marketing: marketing.value,
        });
        submitted.value = true;
    } catch (e) {
        submitting.value = false;
        if (e.response?.status === 422) {
            submitErrors.value = e.response.data.errors;
        }
    } finally {
        submitting.value = false;
    }
}

const mode = computed(() => {
    if (triviaStore.gameState === "results") return "final";
    if (triviaStore.gameState === "playing" && triviaStore.hasCorrectGuess)
        return "round";
    return null;
});

const visible = computed(() => mode.value !== null);

const activeRoundIndex = computed(() => triviaStore.resultsRoundView);
const activeRoundStats = computed(() =>
    triviaStore.roundStatsAt(activeRoundIndex.value),
);

const formatNumber = (n) => n.toLocaleString("en-US");

const totalStats = computed(() => [
    {
        icon: IconClock,
        label: "Total Time",
        value: triviaStore.formattedTotalFullTime,
    },
    {
        icon: IconCheckmark,
        label: "Total Guesses",
        value: formatNumber(triviaStore.totalGuesses),
    },
    {
        icon: IconTrophy,
        label: "Total Points",
        value: formatNumber(triviaStore.totalPoints),
    },
]);

const roundStatsCards = computed(() => {
    const s = activeRoundStats.value;
    if (!s) return [];
    return [
        { icon: IconClock, label: "Round Time", value: s.time },
        {
            icon: IconCheckmark,
            label: "Round Guesses",
            value: String(s.guessCount),
        },
        {
            icon: IconTrophy,
            label: "Round Points",
            value: formatNumber(s.roundPoints),
        },
    ];
});

const roundButton = computed(() => {
    if (triviaStore.isLastRound) {
        return {
            label: "View Results",
            action: () => triviaStore.showResults(),
        };
    }
    return { label: "Next Round", action: () => triviaStore.nextRound() };
});
</script>

<template>
    <Transition name="fade" appear>
        <div
            v-if="visible"
            class="trivia-results bg-marine-800 border-marine-500 flex flex-col overflow-hidden border-r border-b"
        >
            <!-- FINAL MODE -->
            <template v-if="mode === 'final'">
                <div
                    class="flex flex-1 flex-col items-center overflow-y-auto px-6 py-12 max-lg:gap-8 max-lg:px-4 max-lg:py-8 lg:gap-14"
                >
                    <!-- Heading -->
                    <div
                        class="flex flex-col items-center gap-3 text-center uppercase"
                    >
                        <p class="h8 leading-[0.9] font-bold">Trivia</p>
                        <h2
                            class="final-score-title h1 leading-[0.75] font-extrabold text-yellow-400"
                        >
                            Final Score
                        </h2>
                    </div>

                    <!-- Stats + Form -->
                    <div class="flex w-full flex-col gap-12 max-lg:gap-8">
                        <!-- Total stats -->
                        <div
                            class="grid w-full gap-2 max-lg:grid-cols-2 lg:grid-cols-3"
                        >
                            <div
                                v-for="(stat, idx) in totalStats"
                                :key="stat.label"
                                :class="[
                                    'border-marine-500 flex h-30 flex-col justify-center gap-4 border p-4',
                                    idx === 2 && 'max-lg:col-span-2',
                                ]"
                            >
                                <component
                                    :is="stat.icon"
                                    class="size-6 text-yellow-400"
                                />
                                <div class="flex flex-col gap-4">
                                    <p
                                        class="body-sm faded-text leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                                    >
                                        {{ stat.label }}
                                    </p>
                                    <p
                                        class="h4 leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                                    >
                                        {{ stat.value }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col gap-4">
                            <!-- Submission success -->
                            <template v-if="submitted">
                                <p
                                    class="body-sm text-temperature-correct text-center leading-[0.9] font-bold uppercase"
                                >
                                    Score submitted!
                                </p>
                            </template>

                            <!-- Form -->
                            <template v-else>
                                <div class="flex flex-col gap-4">
                                    <p
                                        class="body-sm faded-text leading-[0.9] font-bold uppercase"
                                    >
                                        Enter your details to be added to
                                        leaderboard
                                    </p>
                                    <div class="flex flex-col gap-1">
                                        <TextInput
                                            v-model="username"
                                            placeholder="USERNAME"
                                            input-class="w-full"
                                        />
                                        <p
                                            v-if="submitErrors?.username"
                                            class="body-sm text-red-400"
                                        >
                                            {{ submitErrors?.username?.[0] }}
                                        </p>
                                        <TextInput
                                            v-model="email"
                                            type="email"
                                            placeholder="YOUR EMAIL"
                                            input-class="w-full"
                                        />
                                        <p
                                            v-if="submitErrors?.email"
                                            class="body-sm text-red-400"
                                        >
                                            {{ submitErrors?.email?.[0] }}
                                        </p>
                                        <Button
                                            variant="secondary"
                                            className="w-full!"
                                            :disabled="!canSubmit || submitting"
                                            @click="handleSubmit"
                                        >
                                            {{
                                                submitting
                                                    ? "Submitting…"
                                                    : "Submit"
                                            }}
                                        </Button>
                                    </div>
                                </div>

                                <div class="flex flex-col gap-4">
                                    <div class="flex items-center gap-2">
                                        <IconInfoCircle
                                            class="size-3 shrink-0 opacity-50"
                                        />
                                        <p
                                            class="body-sm faded-text leading-[0.9]"
                                        >
                                            We collect email to power site
                                            functionality
                                        </p>
                                    </div>

                                    <label class="flex items-start gap-2">
                                        <button
                                            :class="[
                                                'border-marine-500 bg-marine-800 flex size-5 shrink-0 items-center justify-center overflow-clip border',
                                                marketing &&
                                                    'border-yellow-400 bg-yellow-400',
                                            ]"
                                            @click.prevent="
                                                marketing = !marketing
                                            "
                                        >
                                            <svg
                                                v-if="marketing"
                                                width="12"
                                                height="12"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                class="text-marine-800"
                                            >
                                                <path
                                                    d="M16.6668 5L7.50016 14.1667L3.3335 10"
                                                    stroke="currentColor"
                                                    stroke-width="3"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </button>
                                        <span
                                            class="body-sm faded-text leading-[1.2]"
                                        >
                                            Receive the latest updates on
                                            Exspeedia.com, campaign related
                                            material, as well as deals, offers
                                            and other information from Expedia
                                            via email. You can opt out of
                                            marketing emails anytime.
                                        </span>
                                    </label>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="shrink-0 p-6 max-lg:p-0">
                    <Button
                        :disabled="triviaStore.loading"
                        className="w-full!"
                        @click="triviaStore.playAgain()"
                    >
                        {{ triviaStore.loading ? "Loading…" : "Play Again" }}
                    </Button>
                </div>
            </template>

            <!-- ROUND MODE -->
            <template v-else-if="mode === 'round'">
                <div
                    class="flex flex-1 flex-col gap-6 overflow-y-auto p-6 max-lg:p-4"
                >
                    <!-- Round tabs -->
                    <div class="border-marine-500 flex h-9 border-b">
                        <button
                            v-for="i in triviaStore.totalRounds"
                            :key="i"
                            :disabled="!triviaStore.roundStatsAt(i - 1)"
                            :class="[
                                'body-sm relative flex flex-1 items-center justify-center px-2 py-3 font-bold uppercase',
                                activeRoundIndex === i - 1
                                    ? 'text-yellow-400'
                                    : 'font-medium opacity-30',
                                !triviaStore.roundStatsAt(i - 1) &&
                                    'cursor-not-allowed',
                            ]"
                            @click="triviaStore.setResultsRoundView(i - 1)"
                        >
                            Round {{ i }}
                            <span
                                v-if="activeRoundIndex === i - 1"
                                class="absolute right-0 bottom-0 left-0 h-0.5 bg-yellow-400"
                            ></span>
                        </button>
                    </div>

                    <!-- Bonuses -->
                    <div
                        v-if="activeRoundStats"
                        class="border-marine-500 body-md flex flex-col gap-4 border p-4 leading-[0.9] font-bold uppercase"
                    >
                        <div class="flex items-center justify-between">
                            <span class="faded-text">Speed Bonus</span>
                            <span class="text-temperature-correct">
                                +{{ formatNumber(activeRoundStats.speedBonus) }}
                            </span>
                        </div>
                        <div class="flex items-center justify-between">
                            <span class="faded-text">Accuracy Bonus</span>
                            <span class="text-temperature-correct">
                                +{{
                                    formatNumber(activeRoundStats.accuracyBonus)
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Round stats -->
                    <div
                        v-if="activeRoundStats"
                        class="border-marine-500 flex gap-2 border px-3 pt-3 pb-6"
                    >
                        <div
                            v-for="stat in roundStatsCards"
                            :key="stat.label"
                            class="flex flex-1 flex-col items-center justify-center gap-4 px-3"
                        >
                            <component
                                :is="stat.icon"
                                class="size-6 text-yellow-400"
                            />
                            <p
                                class="body-sm faded-text leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                            >
                                {{ stat.label }}
                            </p>
                            <p
                                class="h4 leading-[0.9] font-bold tracking-[-0.02em] uppercase"
                            >
                                {{ stat.value }}
                            </p>
                        </div>
                    </div>

                    <!-- Guess list -->
                    <div class="flex flex-col gap-1">
                        <div
                            v-for="(guess, i) in activeRoundStats?.guesses ??
                            []"
                            :key="i"
                            class="border-marine-500 relative grid h-10 grid-cols-[1fr_1fr_0.75fr] items-center gap-2 border border-l-2 px-4"
                            :style="{ borderLeftColor: guess.temperatureHex }"
                        >
                            <span
                                class="body-sm leading-[0.9] font-bold uppercase"
                            >
                                {{ guess.countryName }}
                            </span>
                            <span
                                class="body-sm faded-text text-right leading-[0.8] font-medium uppercase"
                            >
                                {{ guess.distance }}
                            </span>
                            <span
                                class="body-sm flex items-center justify-end gap-1 leading-[0.9] font-bold uppercase"
                                :style="{ color: guess.temperatureHex }"
                            >
                                {{ guess.temperatureName }}
                                <IconCheckmark
                                    v-if="guess.correct"
                                    class="size-4"
                                />
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Action button -->
                <div class="border-marine-500 border-t p-6 max-lg:p-0">
                    <Button
                        :disabled="triviaStore.loading"
                        className="w-full!"
                        @click="roundButton.action()"
                    >
                        {{
                            triviaStore.loading ? "Loading…" : roundButton.label
                        }}
                    </Button>
                </div>
            </template>
        </div>
    </Transition>
</template>

<style scoped>
.trivia-results {
    grid-column: 4 / -9;
    grid-row: 3 / -2;
    z-index: 4;
}

@media (max-width: 1024px) {
    .trivia-results {
        grid-column: 1 / -1;
        grid-row: 2 / -1;
    }
}

.final-score-title {
    text-shadow: 0 0.140625em 0 var(--marine-base, #19233b);
    -webkit-text-stroke-width: 0.09375em;
    -webkit-text-stroke-color: var(--color-marine-600, #19233b);
    paint-order: stroke fill;
}
</style>
